import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn, KPI, KPIRow, Loading, Msg, Section, Select, TextInput, panelGet, useAdminGet } from '../kit';
import { type Period, PeriodPicker, periodError, periodText, presetRange } from '../period';
import {
  ChangeView, Collapse, DiffView, EVENT_SERIES, Fact, Facts, FieldChips, LineChart, Pill, type RecrawlCtx, RunLink, S,
  SplitBar, StackedColumns, type Tone, arr, bucketLabel, eventColor, fmtIST, fmtN, fmtPct, n0, num, obj, str,
} from './shared';
import { ChangeHeatmap } from './Heatmap';

/**
 * Recrawl → Changes: what changed in the corpus over time, from the master's
 * events (recrawl.master_events: the engine's runs plus the history
 * backfilled from the SQLite snapshots).
 *
 *  - Change heatmap (Heatmap.tsx; GET heatmap / heatmap_cell / heatmap_export): months or
 *    weeks × universities, sectors or change types for the same dates and university, a count
 *    picker (all, added, …, net) and an origin filter; a cell, a period or a column opens the
 *    technologies behind it, each with a "History" link that loads it below.
 *  - Timeline (GET timeline {code?, from, to, bucket}): added / modified /
 *    removed / re-added / moved per day, week or month, overall or for one
 *    university; click a column to zoom into it. A move is the same
 *    technology under a new URL: never an addition or a removal, so the net
 *    listed change leaves it out.
 *  - Listed technologies over the same period (rebuilt backwards from today's
 *    master count and the events, see insights.coverage).
 *  - Leaderboard: universities ranked by changes in the period, with the
 *    split and their share of all changes.
 *  - One technology (GET tech_history {url|id}): every version and event with
 *    the field-level before / after; a former URL (previous_urls) finds the
 *    technology too. Titles are looked up in the master
 *    (recrawl/tech_search, removed technologies included) first, since the
 *    history is keyed by URL and id.
 *
 * Hidden events are left out, as on the public What's New.
 */

const PRESETS = ['30d', '90d', '180d', '365d', '730d', 'all', 'custom'];
const PRESET_LABELS: Record<string, string> = {
  '30d': 'Last 30 days', '90d': 'Last 90 days', '180d': 'Last 6 months', '365d': 'Last 12 months',
  '730d': 'Last 2 years', all: 'All history', custom: 'Custom range',
};
const PRESET_DAYS: Record<string, number | null> = { '30d': 30, '90d': 90, '180d': 180, '365d': 365, '730d': 730, all: null };
/** "All history" starts before the oldest SQLite snapshot the master was backfilled from (2025-07). */
const ALL_FROM = '2025-01-01';
const MAIN = EVENT_SERIES.filter(e => ['added', 'modified', 'removed', 'readded', 'moved'].includes(e.key));
/** Today as an India calendar day ('YYYY-MM-DD'). The engine reads a date-only `to` as that IST day,
 *  included (insights._range), so a UTC "today" ended the window at yesterday (IST) from 00:00 to
 *  05:30 IST — just when the nightly 03:00 IST runs promote. India has no daylight saving: a fixed +5:30. */
const today = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const bucketEnd = (b: string, bucket: string) => {
  if (bucket === 'week') return addDays(b, 6);
  if (bucket === 'month') {
    const [y, m] = b.split('-').map(Number);
    return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
  }
  return b;
};
const getPath = (o: any, path: string) => path.split('.').reduce((a, k) => (a && typeof a === 'object' ? a[k] : undefined), o);
const clip = (v: any) => {
  if (typeof v === 'string' && v.length > 600) return `${v.slice(0, 600)}… (${v.length.toLocaleString()} chars)`;
  return v;
};
const STATUS_TONE: Record<string, Tone> = { active: 'green', missing: 'amber', removed: 'red' };
/** master_versions.change_kind → pill tone. */
const VERSION_TONE: Record<string, Tone> = { added: 'green', removed: 'red', readded: 'purple', moved: 'navy' };
const UrlA: React.FC<{ url: any }> = ({ url }) => (
  <a href={str(url)} target="_blank" rel="noreferrer noopener" className="text-primary-700 hover:underline [overflow-wrap:anywhere]">{str(url) || '—'}</a>
);

// ---------------------------------------------------------------------------
// one technology
// ---------------------------------------------------------------------------
const TechHistory: React.FC<{ ctx: RecrawlCtx; initial: { q: string; n: number } | null }> = ({ ctx, initial }) => {
  const [q, setQ] = useState('');
  const [hist, setHist] = useState<any>(null);
  const [cands, setCands] = useState<any[] | null>(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const isKey = (s: string) => /^https?:\/\//i.test(s) || s.includes('/') || /^UTECH-/i.test(s);

  const loadHistory = async (key: string) => {
    const k = key.trim();
    if (!k) return;
    setBusy('history'); setErr(''); setCands(null);
    const { data, error } = await panelGet(S, 'tech_history', /^UTECH-/i.test(k) ? { id: k } : { url: k });
    setBusy('');
    if (error) { setErr(error); setHist(null); return; }
    setHist(data || {});
  };
  const search = async () => {
    const s = q.trim();
    if (!s) return;
    if (isKey(s)) { loadHistory(s); return; }
    setBusy('search'); setErr(''); setHist(null);
    const { data, error } = await panelGet('recrawl', 'tech_search', { q: s, limit: 25 });
    setBusy('');
    if (error) { setErr(error); setCands(null); return; }
    setCands(arr(obj(data).rows));
  };
  useEffect(() => {
    if (initial?.q) { setQ(initial.q); loadHistory(initial.q); }
  }, [initial?.n]);  // eslint-disable-line react-hooks/exhaustive-deps

  const h = obj(hist);
  const t = h.technology && typeof h.technology === 'object' ? h.technology : null;
  const live = obj(h.live);
  const payload = obj(t?.payload);
  const title = str(payload.title || payload.utech_title || live.title || obj(t?.source_payload).title);
  const events = arr(h.events).slice().reverse();
  const versions = arr(h.versions);

  return (
    <Section title="One technology — every version and change">
      <form className="flex items-center gap-2 flex-wrap" onSubmit={e => { e.preventDefault(); search(); }}>
        <TextInput value={q} onChange={setQ} wide placeholder="Paste a technology URL or a UTECH id, or type part of a title" />
        <button type="submit" disabled={!q.trim() || !!busy}
          className="py-1.5 px-4 text-sm rounded-full font-grotesk font-medium bg-primary-500 text-grayscale-100 disabled:bg-primary-100 disabled:text-primary-300">
          {busy === 'search' ? 'Searching…' : busy === 'history' ? 'Loading…' : 'Show history'}
        </button>
      </form>
      <div className="text-[11px] text-grayscale-1100">
        A URL or id goes straight to the master; a title is searched in the master too, removed technologies included.
      </div>
      {err && <Msg kind="error">{err}</Msg>}
      {cands && (
        <div className="flex flex-col gap-1">
          <div className="text-xs text-grayscale-1100">{cands.length ? `${cands.length} match${cands.length === 1 ? '' : 'es'} in the master — pick one:` : 'No technology in the master matches that.'}</div>
          {cands.map((c, i) => (
            <button key={i} type="button" onClick={() => { const k = str(c.utech_technology_id) || str(c.url); setQ(k); loadHistory(k); }}
              className="flex items-center gap-2 text-left rounded px-2 py-1 hover:bg-grayscale-200 border border-grayscale-300">
              <code className="text-[11px] font-mono text-grayscale-1100 shrink-0">{str(c.utech_technology_id) || 'no live id'}</code>
              <span className="text-sm text-grayscale-1300 truncate flex-1">{str(c.title)}</span>
              <span className="text-xs text-grayscale-1100 truncate max-w-[14rem]">{str(c.university_name || c.university_code)}</span>
              {str(c.status) && str(c.status) !== 'active' && <span className="text-[11px] text-grayscale-1100 shrink-0">{str(c.status)}</span>}
            </button>
          ))}
          {!cands.length && <div><Btn small onClick={() => loadHistory(q)}>Look it up in the master as a URL anyway</Btn></div>}
        </div>
      )}
      {hist && !t && (
        <div className="text-sm text-grayscale-1200">
          Not in the master.
          {h.in_live && <> It is live (<code>{str(live.utech_technology_id)}</code>, {str(live.university_name)}) but the master has no record of it — Settings → Reconcile records such rows.</>}
        </div>
      )}
      {t && (
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-2 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-base font-bold text-grayscale-1300 font-grotesk">{title || '(no title)'}</div>
              <a href={str(t.url)} target="_blank" rel="noreferrer noopener" className="text-xs text-primary-700 hover:underline break-all">{str(t.url)}</a>
            </div>
            <Pill tone={STATUS_TONE[str(t.status)] || 'grey'}>{str(t.status) || '—'}</Pill>
            <Pill tone={h.in_live ? 'green' : 'grey'}>{h.in_live ? 'on the live site' : 'not live'}</Pill>
            {h.matched_by && <span className="text-[11px] text-grayscale-1100">matched by {str(h.matched_by).replace(/_/g, ' ')}</span>}
          </div>
          <Facts>
            <Fact label="UTECH id">{str(t.utech_technology_id) || '—'}</Fact>
            <Fact label="University">{str(t.university_code) ? <button type="button" className="text-primary-700 hover:underline" onClick={() => ctx.openUniversity(str(t.university_code))}>{str(t.university_code)}</button> : '—'}{t.university_name ? ` · ${str(t.university_name)}` : ''}</Fact>
            <Fact label="First seen">{fmtIST(t.first_seen_at, false)}</Fact>
            <Fact label="Last seen">{fmtIST(t.last_seen_at)}</Fact>
            <Fact label="Last changed">{fmtIST(t.last_changed_at)}</Fact>
            <Fact label="Version">{fmtN(t.current_version)}</Fact>
            {t.removed_at && <Fact label="Removed">{fmtIST(t.removed_at)}</Fact>}
            {t.readded_at && <Fact label="Re-added">{fmtIST(t.readded_at)}</Fact>}
            {n0(t.removal_count) > 0 && <Fact label="Times removed">{fmtN(t.removal_count)}</Fact>}
            {n0(t.missing_streak) > 0 && <Fact label="Missing streak">{fmtN(t.missing_streak)} run(s)</Fact>}
            {arr(t.legacy_ids).length > 0 && <Fact label="Ids before the 2025-12 rebuild" wide>{arr(t.legacy_ids).join(', ')}</Fact>}
            {arr(t.previous_urls).length > 0 && (
              <Fact label="Previous URLs" wide>
                <span className="flex flex-col">{arr(t.previous_urls).map((u, i) => <UrlA key={i} url={u} />)}</span>
              </Fact>
            )}
          </Facts>

          <div className="flex flex-col gap-1">
            <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">Events ({events.length}, newest first)</div>
            {!events.length && <div className="text-sm text-grayscale-1100">No events.</div>}
            <div className="flex flex-col border-l-2 border-grayscale-200 ml-1.5">
              {events.map((e, i) => (
                <div key={i} className="relative pl-4 py-1.5">
                  <span className="absolute -left-[7px] top-2.5 w-3 h-3 rounded-full border-2 border-white" style={{ background: eventColor(str(e.event_type)) }} />
                  <div className="flex items-center gap-2 flex-wrap text-sm">
                    <b style={{ color: eventColor(str(e.event_type)) }}>{str(e.event_type).replace('_', ' ')}</b>
                    <span className="text-xs text-grayscale-1200">{fmtIST(e.occurred_at)}</span>
                    <span className="text-[11px] text-grayscale-1100">{str(e.source)}</span>
                    {num(e.run_id) !== null && <RunLink ctx={ctx} id={e.run_id} />}
                    {e.hidden && <Pill tone="grey">hidden</Pill>}
                    <FieldChips fields={e.changed_fields} quiet />
                  </div>
                  {str(e.event_type) === 'moved' && (obj(obj(e.diff).university_technology_url).before || obj(obj(e.diff).university_technology_url).after) && (
                    <div className="text-xs text-grayscale-1200">
                      <UrlA url={obj(obj(e.diff).university_technology_url).before} />{' → '}<UrlA url={obj(obj(e.diff).university_technology_url).after} />
                    </div>
                  )}
                  {e.note && <div className="text-xs text-grayscale-1200">{str(e.note)}</div>}
                  {Object.keys(obj(e.diff)).length > 0 && (
                    <div className="mt-1"><Collapse title={`Before / after (${Object.keys(obj(e.diff)).length} field${Object.keys(obj(e.diff)).length === 1 ? '' : 's'})`}>
                      <DiffView diff={e.diff} fields={e.changed_fields} />
                    </Collapse></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {versions.length > 0 && (
            <div className="flex flex-col gap-1">
              <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">Versions ({versions.length})</div>
              {versions.map((v, i) => {
                const prev = i > 0 ? versions[i - 1] : null;
                const fields = arr(v.changed_fields).map(str).filter(Boolean);
                const pick = (x: any) => (x ? (Object.keys(obj(x.source_payload)).length ? x.source_payload : x.payload) : null);
                return (
                  <Collapse key={i} title={
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      <b>v{str(v.version)}</b>
                      <Pill tone={VERSION_TONE[str(v.change_kind)] || 'blue'}>{str(v.change_kind) || '—'}</Pill>
                      <span className="text-xs text-grayscale-1100 font-normal">{fmtIST(v.valid_from, false)} → {v.valid_to ? fmtIST(v.valid_to, false) : 'now'}</span>
                      {fields.length > 0 && <FieldChips fields={fields} max={4} />}
                    </span>
                  } right={num(v.run_id) !== null ? <RunLink ctx={ctx} id={v.run_id} /> : undefined}>
                    {prev && fields.length > 0 ? (
                      <div className="flex flex-col gap-1.5">
                        {fields.map(f => (
                          <ChangeView key={f} field={f} change={{ before: clip(getPath(pick(prev), f)), after: clip(getPath(pick(v), f)) }} />
                        ))}
                      </div>
                    ) : (
                      <div className="text-xs text-grayscale-1100">{prev ? 'No changed fields recorded for this version.' : 'The first version.'}</div>
                    )}
                  </Collapse>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// the tab
// ---------------------------------------------------------------------------
const Changes: React.FC<{ ctx: RecrawlCtx; focus: ({ code?: string; tech?: string; n: number }) | null }> = ({ ctx, focus }) => {
  const [period, setPeriod] = useState<Period>({ preset: '365d', from: '', to: '' });
  const [bucketPref, setBucketPref] = useState('auto');
  const [code, setCode] = useState('');
  const [techReq, setTechReq] = useState<{ q: string; n: number } | null>(null);
  const histRef = useRef<HTMLDivElement>(null);
  const showHistory = (k: string) => {
    if (!k) return;
    setTechReq(r => ({ q: k, n: (r?.n || 0) + 1 }));
    setTimeout(() => histRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);
  };
  useEffect(() => {
    if (!focus) return;
    setCode(str(focus.code).toUpperCase());
    if (focus.tech) setTechReq(r => ({ q: str(focus.tech), n: (r?.n || 0) + 1 }));
  }, [focus?.n]);  // eslint-disable-line react-hooks/exhaustive-deps

  const lastRange = useRef<{ from: string; to: string }>({ from: addDays(today(), -364), to: today() });
  const range = useMemo(() => {
    if (period.preset === 'custom') {
      if (!periodError(period)) lastRange.current = { from: period.from || ALL_FROM, to: period.to || today() };
      return lastRange.current;
    }
    const r = period.preset === 'all' ? { from: ALL_FROM, to: today() } : presetRange(period.preset, PRESET_DAYS, today);
    lastRange.current = { from: r.from || ALL_FROM, to: r.to || today() };
    return lastRange.current;
  }, [period]);
  const spanDays = Math.max(1, Math.round((Date.parse(range.to) - Date.parse(range.from)) / 86400000) + 1);
  const bucket = bucketPref !== 'auto' ? bucketPref : spanDays <= 62 ? 'day' : spanDays <= 400 ? 'week' : 'month';

  const params: Record<string, string> = { from: range.from, to: range.to, bucket };
  if (code) params.code = code;
  const res = useAdminGet<any>(S, 'timeline', params, [range.from, range.to, bucket, code, ctx.tick]);
  const d = obj(res.data);
  const rows = arr(d.timeline);
  const tot = obj(d.totals);
  const cov = obj(d.coverage);
  const byUni = useMemo(() => arr(d.by_university).map(r => ({
    ...r, code: str(r.code).toUpperCase(), total: n0(r.added) + n0(r.modified) + n0(r.removed) + n0(r.readded) + n0(r.moved),
  })).filter(r => r.total > 0).sort((a, b) => b.total - a.total), [d.by_university]);
  const allChanges = n0(tot.added) + n0(tot.modified) + n0(tot.removed) + n0(tot.readded) + n0(tot.moved);
  const net = n0(tot.added) + n0(tot.readded) - n0(tot.removed);
  const maxUni = Math.max(1, ...byUni.map(r => r.total));
  const covPoints = useMemo(() => {
    const buckets = arr(cov.buckets).map(str);
    const series = code ? arr(cov.series).find(s => str(obj(s).code).toUpperCase() === code) : null;
    const ys = series ? arr(obj(series).points) : arr(cov.total);
    return buckets.map((b, i) => ({ x: b, y: n0(ys[i]) }));
  }, [cov, code]);

  const codeOpts = ['', ...ctx.universities.map(u => u.code)];
  if (code && !codeOpts.includes(code)) codeOpts.push(code);
  const codeLabels: Record<string, string> = { '': 'All universities' };
  ctx.universities.forEach(u => { codeLabels[u.code] = `${u.code} — ${u.name}`; });
  const uni = code ? ctx.uniByCode.get(code) : undefined;
  const where = code ? (uni ? `${uni.name} (${code})` : code) : 'all universities';

  return (
    <>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={code} onChange={setCode} options={codeOpts} labels={codeLabels} />
          <Select value={bucketPref} onChange={setBucketPref} options={['auto', 'day', 'week', 'month']}
            labels={{ auto: `Auto (${bucket})`, day: 'Per day', week: 'Per week', month: 'Per month' }} />
          {code && <Btn small onClick={() => ctx.openUniversity(code)}>University details</Btn>}
        </div>
        <PeriodPicker value={period} onChange={setPeriod} options={PRESETS} labels={PRESET_LABELS} presetDays={PRESET_DAYS}
          today={today} hint="Both days included (India calendar days)." />
      </div>

      <KPIRow>
        <KPI label="Added" value={fmtN(tot.added)} hint={`${periodText(period, PRESET_LABELS)} · ${where}`} />
        <KPI label="Modified" value={fmtN(tot.modified)} hint={allChanges ? `${fmtPct(tot.modified, allChanges)} of changes` : undefined} />
        <KPI label="Removed" value={fmtN(tot.removed)} hint={allChanges ? `${fmtPct(tot.removed, allChanges)} of changes` : undefined} />
        <KPI label="Re-added" value={fmtN(tot.readded)} hint="came back after a removal" />
        <KPI label="Moved" value={fmtN(tot.moved)} hint="same technology under a new URL — not added, not removed" />
        <KPI label="Net listed change" value={`${net >= 0 ? '+' : ''}${net.toLocaleString()}`} hint="added + re-added − removed" />
        <KPI label="Missing events" value={fmtN(tot.missing)} hint="absent from a run, not yet removed" />
      </KPIRow>

      <ChangeHeatmap ctx={ctx} range={range} rangeText={periodText(period, PRESET_LABELS)} code={code || undefined}
        onHistory={showHistory} />

      <Section title={`Changes per ${bucket} — ${where}`}
        right={res.loading ? <span className="text-xs text-grayscale-1100">Loading…</span> : <span className="text-xs text-grayscale-1100">click a column to zoom into it</span>}>
        {res.error && <Msg kind="error">{res.error}</Msg>}
        {res.loading && !res.data ? <Loading /> : (
          <StackedColumns rows={rows} xKey="bucket" series={EVENT_SERIES} initiallyOff={['missing', 'manual_fix']} height={200}
            xLabel={(x, long) => bucketLabel(x, bucket, long)} unit="events"
            onPick={r => {
              const b = str(r.bucket);
              if (!b || (bucket === 'day' && period.preset === 'custom' && period.from === b && period.to === b)) return;
              setPeriod({ preset: 'custom', from: b, to: bucketEnd(b, bucket) > today() ? today() : bucketEnd(b, bucket) });
              setBucketPref('auto');
            }}
            empty={`No master events for ${where} in this period.`} />
        )}
      </Section>

      <div className="grid gap-4 lg:grid-cols-2 items-start">
        <Section title={code ? `${code} in this period` : `Leaderboard — ${byUni.length} universities changed`}>
          {code ? (
            <div className="flex flex-col gap-2 text-sm">
              {MAIN.map(s => (
                <div key={s.key} className="flex items-center gap-2">
                  <span className="w-24 text-grayscale-1200">{s.label}</span>
                  <div className="flex-1"><SplitBar parts={[{ key: s.key, value: n0(tot[s.key]), color: s.color }]} total={Math.max(1, allChanges)} /></div>
                  <span className="w-28 text-right"><b>{fmtN(tot[s.key])}</b> <span className="text-xs text-grayscale-1100">({fmtPct(tot[s.key], allChanges)})</span></span>
                </div>
              ))}
              <div><Btn small onClick={() => setCode('')}>Back to all universities</Btn></div>
            </div>
          ) : !byUni.length ? <div className="text-sm text-grayscale-1100">No changes in this period.</div> : (
            <div className="flex flex-col gap-1 max-h-[28rem] overflow-auto">
              {byUni.map((r, i) => (
                <button key={r.code} type="button" onClick={() => setCode(r.code)} title={`${r.name || r.code}: +${n0(r.added)} ~${n0(r.modified)} −${n0(r.removed)} ↺${n0(r.readded)} ↪${n0(r.moved)} — click to focus`}
                  className="flex items-center gap-2 text-left rounded px-1 py-0.5 hover:bg-primary-100/40">
                  <span className="w-6 text-right text-[11px] text-grayscale-1100">{i + 1}</span>
                  <span className="w-44 truncate text-sm"><b>{r.code}</b> <span className="text-xs text-grayscale-1100">{str(r.name)}</span></span>
                  <div className="flex-1 min-w-[4rem]">
                    <div style={{ width: `${Math.max(2, (r.total / maxUni) * 100)}%` }}>
                      <SplitBar parts={MAIN.map(s => ({ key: s.key, value: n0(r[s.key]), color: s.color, label: s.label }))} />
                    </div>
                  </div>
                  <span className="w-28 text-right text-sm"><b>{r.total.toLocaleString()}</b> <span className="text-xs text-grayscale-1100">({fmtPct(r.total, allChanges)})</span></span>
                </button>
              ))}
            </div>
          )}
        </Section>
        <Section title={`Listed technologies — ${where}`}>
          <div className="text-[11px] text-grayscale-1100">Active + missing at the end of each {bucket}, rebuilt backwards from today's master count and the events.</div>
          <LineChart points={covPoints} xLabel={x => bucketLabel(x, bucket, true)} />
        </Section>
      </div>

      <div ref={histRef} className="scroll-mt-4"><TechHistory ctx={ctx} initial={techReq} /></div>
    </>
  );
};

export default Changes;
